import { useMemo, useState, useRef, useCallback } from 'react';
import {
  QuotaData,
  AWS_QUOTA_GROUPS,
  deduplicateForAggregation,
  getFamilyBarColor,
  getFamilyDisplayName,
  getQuotaGroup,
} from './quotaUtils';

// RGB values for bokeh glow — mirrors familyColors in quotaUtils
const familyGlowRgb: Record<string, string> = {
  'g4dn': '37, 99, 235',
  'g4ad': '2, 132, 199',
  'g5': '22, 163, 74',
  'g5g': '234, 88, 12',
  'p4d': '147, 51, 234',
  'p4de': '219, 39, 119',
  'p5': '225, 29, 72',
  'a100': '220, 38, 38',
  'a100-80gb': '185, 28, 28',
  'v100': '202, 138, 4',
  't4': '79, 70, 229',
  'l4': '5, 150, 105',
  'h100': '8, 145, 178',
  'p100': '217, 119, 6',
  'k80': '71, 85, 105',
  'G/VT Instances': '37, 99, 235',
  'P Instances': '147, 51, 234',
};

function getGlowRgb(family: string): string {
  return familyGlowRgb[family] || '156, 163, 175';
}

interface FamilyDetail {
  limit: number;
  usage: number;
  available: number;
  utilization: number;
  usage_trend: string;
  quota_increase_requested: boolean;
}

interface RegionRow {
  provider: string;
  region: string;
  display_name: string;
  families: Record<string, FamilyDetail>;
  total_limit: number;
  total_usage: number;
  total_available: number;
}

interface HoveredSegment {
  regionKey: string;
  family: string;
  rect: DOMRect;
}

interface Props {
  quotas: QuotaData[];
  height?: number;
  onRequestIncrease?: (quota: QuotaData, groupLabel?: string, groupFamilies?: string[]) => void;
}

export default function QuotaStackedBar({ quotas, height = 300, onRequestIncrease }: Props) {
  const [hoveredSegment, setHoveredSegment] = useState<HoveredSegment | null>(null);
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const [selectedFamilies, setSelectedFamilies] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  const dedupedQuotas = useMemo(() => deduplicateForAggregation(quotas), [quotas]);

  // Raw (non-deduped) quotas indexed by region for request increase lookups
  const rawQuotasByRegion = useMemo(() => {
    const map: Record<string, QuotaData[]> = {};
    for (const q of quotas) {
      const key = `${q.provider}-${q.region}`;
      if (!map[key]) map[key] = [];
      map[key].push(q);
    }
    return map;
  }, [quotas]);

  const regionData = useMemo(() => {
    const grouped = dedupedQuotas.reduce((acc, quota) => {
      const key = `${quota.provider}-${quota.region}`;
      if (!acc[key]) {
        acc[key] = {
          provider: quota.provider,
          region: quota.region,
          display_name: quota.region_display_name || quota.region,
          families: {},
          total_limit: 0,
          total_usage: 0,
          total_available: 0,
        };
      }

      acc[key].families[quota.instance_family] = {
        limit: quota.quota_limit,
        usage: quota.current_usage,
        available: quota.available,
        utilization: quota.utilization_percent,
        usage_trend: quota.usage_trend,
        quota_increase_requested: quota.quota_increase_requested,
      };

      acc[key].total_limit += quota.quota_limit;
      acc[key].total_usage += quota.current_usage;
      acc[key].total_available += quota.available;

      return acc;
    }, {} as Record<string, RegionRow>);

    return Object.entries(grouped)
      .sort(([, a], [, b]) => {
        if (a.provider !== b.provider) return a.provider.localeCompare(b.provider);
        return a.region.localeCompare(b.region);
      })
      .map(([key, val]) => ({ key, ...val }));
  }, [dedupedQuotas]);

  const instanceFamilies = useMemo(() => {
    const families = new Set<string>();
    dedupedQuotas.forEach(q => families.add(q.instance_family));
    return Array.from(families).sort();
  }, [dedupedQuotas]);

  const maxLimit = useMemo(
    () => Math.max(...regionData.map(r => r.total_limit), 1),
    [regionData],
  );

  const handleSegmentEnter = useCallback((regionKey: string, family: string, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHoveredSegment({ regionKey, family, rect });
  }, []);

  const handleSegmentLeave = useCallback(() => {
    setHoveredSegment(null);
  }, []);

  const handleRegionClick = useCallback((regionKey: string) => {
    setExpandedRegion(prev => {
      if (prev === regionKey) return null;
      setSelectedFamilies(new Set());
      return regionKey;
    });
  }, []);

  const toggleFamily = useCallback((family: string) => {
    setSelectedFamilies(prev => {
      const next = new Set(prev);
      if (next.has(family)) next.delete(family);
      else next.add(family);
      return next;
    });
  }, []);

  const handleRequestForFamily = useCallback(
    (regionKey: string, familyName: string) => {
      if (!onRequestIncrease) return;
      const regionQuotas = rawQuotasByRegion[regionKey] || [];

      // Direct match — works for non-grouped families and GCP
      let match = regionQuotas.find(q => q.instance_family === familyName);
      if (match) {
        const group = getQuotaGroup(match.provider, match.instance_family);
        if (group) {
          onRequestIncrease(match, group.label, group.families);
        } else {
          onRequestIncrease(match);
        }
        return;
      }

      // familyName is a deduplicated group label (e.g. "G/VT Instances")
      // Reverse-lookup the group definition and find a matching raw quota
      for (const groupDef of Object.values(AWS_QUOTA_GROUPS)) {
        if (groupDef.label === familyName) {
          match = regionQuotas.find(q => groupDef.families.includes(q.instance_family));
          if (match) {
            onRequestIncrease(match, groupDef.label, groupDef.families);
            return;
          }
        }
      }
    },
    [onRequestIncrease, rawQuotasByRegion],
  );

  const handleBulkRequest = useCallback(
    (regionKey: string) => {
      if (!onRequestIncrease || selectedFamilies.size === 0) return;
      const first = Array.from(selectedFamilies)[0];
      handleRequestForFamily(regionKey, first);
    },
    [onRequestIncrease, selectedFamilies, handleRequestForFamily],
  );

  const getTrendArrow = (trend: string) => {
    if (trend === 'increasing') return '\u2191';
    if (trend === 'decreasing') return '\u2193';
    return '\u2192';
  };

  const getTrendColor = (trend: string) => {
    if (trend === 'increasing') return 'text-orange-400';
    if (trend === 'decreasing') return 'text-green-400';
    return 'text-gray-500';
  };

  if (regionData.length === 0) {
    return (
      <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
        <p className="text-gray-400">No quota data available</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-gray-900 rounded-xl p-6 border border-gray-800 relative">
      <h3 className="text-lg font-semibold text-white mb-4">Regional Quota Utilization</h3>

      {/* Chart */}
      <div className="space-y-1" style={{ minHeight: height }}>
        {regionData.map((region) => {
          const regionKey = region.key;
          const utilizationPercent = region.total_limit > 0
            ? (region.total_usage / region.total_limit * 100).toFixed(1)
            : '0';
          const isExpanded = expandedRegion === regionKey;
          const familyCount = Object.keys(region.families).length;

          return (
            <div key={regionKey}>
              {/* Clickable region row */}
              <div
                className={`group rounded-lg px-3 py-2 cursor-pointer transition-all duration-150 ${
                  isExpanded
                    ? 'bg-gray-800/60 ring-1 ring-gray-700/50'
                    : 'hover:bg-gray-800/40'
                }`}
                onClick={() => handleRegionClick(regionKey)}
              >
                {/* Region Header */}
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <div className="flex items-center space-x-2">
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${
                        isExpanded ? 'rotate-90 text-blue-400' : 'text-gray-600 group-hover:text-gray-400'
                      }`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-white font-medium">{region.display_name}</span>
                    <span className="text-gray-500 text-xs">{region.provider.toUpperCase()}</span>
                    <span className="text-gray-600 text-xs hidden sm:inline">
                      {familyCount} {familyCount === 1 ? 'family' : 'families'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-xs">
                    {(() => {
                      const pendingCount = Object.values(region.families).filter(f => f.quota_increase_requested).length;
                      return pendingCount > 0 ? (
                        <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 bg-yellow-900/30 text-yellow-300 border border-yellow-700/50 rounded">
                          <svg className="w-2 h-2 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx="4" cy="4" r="3" />
                          </svg>
                          <span>{pendingCount} pending</span>
                        </span>
                      ) : null;
                    })()}
                    <span className="text-gray-400">
                      {region.total_usage}/{region.total_limit}
                    </span>
                    <span className={`font-medium ${
                      Number(utilizationPercent) >= 90 ? 'text-red-400' :
                      Number(utilizationPercent) >= 70 ? 'text-yellow-400' :
                      'text-green-400'
                    }`}>
                      {utilizationPercent}%
                    </span>
                    {!isExpanded && (
                      <span className="text-gray-600 group-hover:text-blue-400/70 transition-colors hidden sm:inline">
                        Manage
                      </span>
                    )}
                  </div>
                </div>

                {/* Stacked Bar — two-layer: inner clips segments, outer allows glow bleed */}
                <div className="relative w-full h-8">
                  {/* Clipped bar content */}
                  <div className="absolute inset-0 bg-gray-800 rounded-lg overflow-hidden">
                    <div className="absolute inset-0 flex">
                      {instanceFamilies.map((family) => {
                        const familyData = region.families[family];
                        if (!familyData) return null;

                        const usageWidth = (familyData.usage / maxLimit) * 100;
                        const availableWidth = (familyData.available / maxLimit) * 100;
                        const totalWidth = usageWidth + availableWidth;
                        const isPendingIdle = familyData.quota_increase_requested && familyData.limit === 0;
                        const isFamilyHovered = hoveredSegment?.regionKey === regionKey && hoveredSegment?.family === family;
                        const anyHovered = hoveredSegment !== null;

                        const segmentFilter = isFamilyHovered
                          ? 'brightness(1.45) saturate(1.3)'
                          : anyHovered
                            ? 'brightness(0.4) blur(1.5px) saturate(0.35)'
                            : undefined;

                        return (
                          <div
                            key={family}
                            className="flex"
                            style={{
                              width: isPendingIdle ? undefined : (totalWidth > 0 ? `${totalWidth}%` : undefined),
                              minWidth: isPendingIdle ? '14px' : undefined,
                              maxWidth: isPendingIdle ? '14px' : undefined,
                              position: 'relative',
                              zIndex: isFamilyHovered ? 10 : 1,
                            }}
                          >
                            {/* Pending request with zero limit — show amber placeholder */}
                            {isPendingIdle && (
                              <div
                                className="w-full cursor-default rounded-sm border-l-2 border-yellow-500/60"
                                style={{
                                  background: 'repeating-linear-gradient(135deg, rgba(234,179,8,0.25), rgba(234,179,8,0.25) 2px, transparent 2px, transparent 5px)',
                                  filter: segmentFilter,
                                  transition: 'filter 200ms ease-out',
                                }}
                                onMouseEnter={(e) => { e.stopPropagation(); handleSegmentEnter(regionKey, family, e); }}
                                onMouseLeave={handleSegmentLeave}
                              />
                            )}

                            {/* Usage portion */}
                            {!isPendingIdle && familyData.usage > 0 && (
                              <div
                                className={`${getFamilyBarColor(family, 'usage')} cursor-default`}
                                style={{
                                  width: `${(familyData.usage / (familyData.usage + familyData.available)) * 100}%`,
                                  filter: segmentFilter,
                                  transition: 'filter 200ms ease-out',
                                }}
                                onMouseEnter={(e) => { e.stopPropagation(); handleSegmentEnter(regionKey, family, e); }}
                                onMouseLeave={handleSegmentLeave}
                              />
                            )}

                            {/* Available portion */}
                            {!isPendingIdle && familyData.available > 0 && (
                              <div
                                className={`${getFamilyBarColor(family, 'available')} cursor-default`}
                                style={{
                                  width: `${(familyData.available / (familyData.usage + familyData.available)) * 100}%`,
                                  opacity: isFamilyHovered ? 0.5 : 0.3,
                                  filter: segmentFilter,
                                  transition: 'filter 200ms ease-out, opacity 200ms ease-out',
                                }}
                                onMouseEnter={(e) => { e.stopPropagation(); handleSegmentEnter(regionKey, family, e); }}
                                onMouseLeave={handleSegmentLeave}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Threshold Lines */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-0 bottom-0 w-px bg-yellow-500/50" style={{ left: '70%' }} />
                      <div className="absolute top-0 bottom-0 w-px bg-red-500/50" style={{ left: '90%' }} />
                    </div>
                  </div>

                  {/* Bokeh glow overlay — outside clip so it bleeds past the bar edges */}
                  {hoveredSegment?.regionKey === regionKey && (() => {
                    const hFamily = hoveredSegment.family;
                    const hData = region.families[hFamily];
                    if (!hData) return null;

                    let left = 0;
                    for (const f of instanceFamilies) {
                      if (f === hFamily) break;
                      const fd = region.families[f];
                      if (fd) left += ((fd.usage + fd.available) / maxLimit) * 100;
                    }
                    const width = ((hData.usage + hData.available) / maxLimit) * 100;
                    const rgb = getGlowRgb(hFamily);

                    return (
                      <div
                        className="absolute pointer-events-none rounded-full"
                        style={{
                          left: `${left}%`,
                          width: `${Math.max(width, 3)}%`,
                          top: '-5px',
                          bottom: '-5px',
                          boxShadow: `0 0 18px 5px rgba(${rgb}, 0.55), 0 0 36px 10px rgba(${rgb}, 0.2)`,
                          transition: 'all 200ms ease-out',
                        }}
                      />
                    );
                  })()}
                </div>
              </div>

              {/* Expanded Region Drawer */}
              {isExpanded && (
                <div className="mx-3 mb-3 mt-1 bg-gray-800/40 rounded-lg border border-gray-700/50 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700/50">
                        <th className="text-left p-2.5 text-gray-500 font-medium w-8">
                          <input
                            type="checkbox"
                            className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500/30"
                            checked={selectedFamilies.size > 0 && selectedFamilies.size === Object.keys(region.families).length}
                            onChange={() => {
                              const allFams = Object.keys(region.families);
                              if (selectedFamilies.size === allFams.length) {
                                setSelectedFamilies(new Set());
                              } else {
                                setSelectedFamilies(new Set(allFams));
                              }
                            }}
                          />
                        </th>
                        <th className="text-left p-2.5 text-gray-500 font-medium text-xs uppercase tracking-wider">Family</th>
                        <th className="text-right p-2.5 text-gray-500 font-medium text-xs uppercase tracking-wider">Usage / Limit</th>
                        <th className="text-right p-2.5 text-gray-500 font-medium text-xs uppercase tracking-wider">Utilization</th>
                        <th className="text-center p-2.5 text-gray-500 font-medium text-xs uppercase tracking-wider">Trend</th>
                        <th className="text-center p-2.5 text-gray-500 font-medium text-xs uppercase tracking-wider">Status</th>
                        <th className="text-right p-2.5 text-gray-500 font-medium text-xs uppercase tracking-wider w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(region.families).map(([family, data]) => {
                        const utilColor = data.utilization >= 90
                          ? 'text-red-400'
                          : data.utilization >= 70
                            ? 'text-yellow-400'
                            : 'text-green-400';

                        return (
                          <tr
                            key={family}
                            className="border-b border-gray-700/30 last:border-0 hover:bg-gray-700/20 transition-colors"
                          >
                            <td className="p-2.5">
                              <input
                                type="checkbox"
                                className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500/30"
                                checked={selectedFamilies.has(family)}
                                onChange={() => toggleFamily(family)}
                              />
                            </td>
                            <td className="p-2.5">
                              <div className="flex items-center space-x-2">
                                <div className={`w-2.5 h-2.5 rounded ${getFamilyBarColor(family, 'usage')}`} />
                                <span className="text-white font-medium">{getFamilyDisplayName(region.provider, family)}</span>
                              </div>
                            </td>
                            <td className="p-2.5 text-right">
                              <span className="text-white">{data.usage}</span>
                              <span className="text-gray-500"> / {data.limit}</span>
                            </td>
                            <td className="p-2.5 text-right">
                              <span className={`font-medium ${utilColor}`}>
                                {Math.round(data.utilization)}%
                              </span>
                            </td>
                            <td className="p-2.5 text-center">
                              <span className={getTrendColor(data.usage_trend)}>
                                {getTrendArrow(data.usage_trend)}
                              </span>
                            </td>
                            <td className="p-2.5 text-center">
                              {data.quota_increase_requested ? (
                                <span className="inline-flex items-center space-x-1 text-xs px-1.5 py-0.5 bg-yellow-900/30 text-yellow-300 border border-yellow-700/50 rounded">
                                  <svg className="w-2.5 h-2.5 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
                                    <circle cx="4" cy="4" r="3" />
                                  </svg>
                                  <span>Pending</span>
                                </span>
                              ) : data.utilization >= 80 ? (
                                <span className="text-xs text-yellow-400">High</span>
                              ) : (
                                <span className="text-xs text-gray-500">OK</span>
                              )}
                            </td>
                            <td className="p-2.5 text-right">
                              {!data.quota_increase_requested && onRequestIncrease && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestForFamily(regionKey, family);
                                  }}
                                  className={`text-xs px-2 py-1 rounded transition-colors ${
                                    data.utilization >= 80
                                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                      : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600'
                                  }`}
                                >
                                  Request Increase
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Bulk action footer */}
                  {onRequestIncrease && selectedFamilies.size > 0 && (
                    <div className="px-3 py-2.5 bg-gray-800/60 border-t border-gray-700/50 flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {selectedFamilies.size} {selectedFamilies.size === 1 ? 'family' : 'families'} selected
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBulkRequest(regionKey);
                        }}
                        className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors font-medium"
                      >
                        Request Increase for Selected
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Rich Tooltip */}
      {hoveredSegment && (() => {
        const region = regionData.find(r => r.key === hoveredSegment.regionKey);
        const familyData = region?.families[hoveredSegment.family];
        if (!region || !familyData) return null;

        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return null;

        const tooltipLeft = hoveredSegment.rect.left - containerRect.left + hoveredSegment.rect.width / 2;
        const tooltipTop = hoveredSegment.rect.top - containerRect.top - 8;

        const utilColor = familyData.utilization >= 90
          ? 'text-red-400'
          : familyData.utilization >= 70
            ? 'text-yellow-400'
            : 'text-green-400';

        return (
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              left: `${tooltipLeft}px`,
              top: `${tooltipTop}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="bg-gray-950 border border-gray-700 rounded-lg shadow-xl px-3 py-2.5 min-w-[180px]">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-white font-semibold text-sm">
                  {getFamilyDisplayName(region.provider, hoveredSegment.family)}
                </span>
                <div className={`w-2 h-2 rounded-full ${getFamilyBarColor(hoveredSegment.family, 'usage')}`} />
              </div>

              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Usage / Limit</span>
                  <span className="text-white font-medium">{familyData.usage} / {familyData.limit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Utilization</span>
                  <span className={`font-medium ${utilColor}`}>{Math.round(familyData.utilization)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Available</span>
                  <span className="text-green-400 font-medium">{familyData.available}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Trend</span>
                  <span className={getTrendColor(familyData.usage_trend)}>
                    {getTrendArrow(familyData.usage_trend)} {familyData.usage_trend}
                  </span>
                </div>
              </div>

              {familyData.quota_increase_requested && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700">
                  <span className="inline-flex items-center space-x-1 text-xs text-yellow-300">
                    <svg className="w-2.5 h-2.5 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
                      <circle cx="4" cy="4" r="3" />
                    </svg>
                    <span>Increase Pending</span>
                  </span>
                </div>
              )}
            </div>
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="w-2 h-2 bg-gray-950 border-b border-r border-gray-700 transform rotate-45 -mt-1" />
            </div>
          </div>
        );
      })()}

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="flex flex-wrap gap-4">
          {instanceFamilies.map(family => (
            <div key={family} className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className={`w-3 h-3 rounded ${getFamilyBarColor(family, 'usage')}`} />
                <div className={`w-3 h-3 rounded ${getFamilyBarColor(family, 'available')} opacity-30`} />
              </div>
              <span className="text-xs text-gray-400">{family.toUpperCase()}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
          <span>Solid: In Use</span>
          <span>Light: Available</span>
          <span className="text-yellow-500">| 70% threshold</span>
          <span className="text-red-500">| 90% threshold</span>
        </div>
      </div>
    </div>
  );
}
